import React, { useCallback, useEffect, useRef } from 'react';
import { VStack, Input, Field, Spinner, Center, Text, HStack } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from '../../../../hooks/useTranslation';
import { useGetRegCustomerQuery, usePatchRegCustomerMutation } from '../../../../api';
import type { PatchRegCustomer } from '../../../../api/registration/types';
import { alertManager } from '../../../../utils/alertManager';
import { hasDiff } from '../../../../utils/hasDiff';
import {
    createRegistrationCompanyDetailsSchema,
    RegistrationCompanyDetailsFormData,
} from '../../../../schemas/registrationSchema';
import Button from '../../../../components/base/Button';

type RegistrationStepProps = {
    onBack: () => void;
    onNext: () => void;
};

export const StepCompanyDetails: React.FC<RegistrationStepProps> = ({ onBack, onNext }) => {
    const { t } = useTranslation();
    const { data: regCustomer, isLoading } = useGetRegCustomerQuery();
    const [patchRegCustomer, { isLoading: isPatching }] = usePatchRegCustomerMutation();
    const {
        register,
        reset,
        handleSubmit,
        formState: { errors },
    } = useForm<RegistrationCompanyDetailsFormData>({
        resolver: yupResolver(createRegistrationCompanyDetailsSchema(t)),
        mode: 'onBlur',
    });
    const appliedFromApi = useRef(false);

    useEffect(() => {
        if (isLoading || !regCustomer || appliedFromApi.current) {
            return;
        }
        appliedFromApi.current = true;
        reset({
            companyName: regCustomer.company_name ?? '',
            activity: regCustomer.activity ?? '',
            inn: regCustomer.inn ?? '',
        });
    }, [isLoading, regCustomer, reset]);

    const handleNextClick = useCallback(() => {
        void handleSubmit(async ({ companyName, activity, inn }) => {
            if (isPatching) return;

            const patchPayload: PatchRegCustomer = {
                company_name: companyName,
                activity,
                inn,
            };

            if (hasDiff<PatchRegCustomer>(regCustomer, patchPayload)) {
                try {
                    await patchRegCustomer(patchPayload).unwrap();
                } catch {
                    alertManager.error(t.registration.errors.serverError);
                    return;
                }
            }

            onNext();
        })();
    }, [handleSubmit, isPatching, onNext, patchRegCustomer, regCustomer]);

    if (isLoading) {
        return (
            <Center py={10} flexDirection="column" gap={4}>
                <Spinner size="lg" color="yellow.600" />
                <Text fontSize="md" color="gray.700" textAlign="center">
                    {t.registration.form.loadingRegCustomerData}
                </Text>
            </Center>
        );
    }

    return (
        <VStack align="stretch" w="full" h="full" minH={0} p={1}>
            <VStack
                gap={6}
                align="stretch"
                flex={1}
                minH={0}
                overflowY="auto"
                p={1}
                css={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                }}
            >
                <Field.Root invalid={!!errors.companyName}>
                    <Field.Label fontSize="md" fontWeight="bold" color="gray.800" mb={2}>
                        {t.registration.form.companyNameLabel}
                    </Field.Label>
                    <Input
                        type="text"
                        placeholder={t.registration.form.companyNamePlaceholder}
                        size="lg"
                        h="48px"
                        borderRadius="0"
                        colorPalette={errors.companyName ? 'red' : 'gray'}
                        {...register('companyName')}
                    />
                    {errors.companyName && (
                        <Field.ErrorText color="red.500" fontSize="sm" mt={1}>
                            {errors.companyName.message}
                        </Field.ErrorText>
                    )}
                </Field.Root>

                <Field.Root invalid={!!errors.activity}>
                    <Field.Label fontSize="md" fontWeight="bold" color="gray.800" mb={2}>
                        {t.registration.form.activityLabel}
                    </Field.Label>
                    <Input
                        type="text"
                        placeholder={t.registration.form.activityPlaceholder}
                        size="lg"
                        h="48px"
                        borderRadius="0"
                        colorPalette={errors.activity ? 'red' : 'gray'}
                        {...register('activity')}
                    />
                    {errors.activity && (
                        <Field.ErrorText color="red.500" fontSize="sm" mt={1}>
                            {errors.activity.message}
                        </Field.ErrorText>
                    )}
                </Field.Root>

                <Field.Root>
                    <Field.Label fontSize="md" fontWeight="bold" color="gray.800" mb={2}>
                        {t.registration.form.innLabel} ({t.registration.form.ifAvailable})
                    </Field.Label>
                    <Input
                        type="text"
                        placeholder={t.registration.form.innPlaceholder}
                        size="lg"
                        h="48px"
                        borderRadius="0"
                        {...register('inn')}
                    />
                </Field.Root>
            </VStack>

            <HStack gap={3} justify="space-between" w="full" flexShrink={0} pt={2}>
                <Button buttonType="secondary" size="lg" flex={1} onClick={onBack}>
                    {t.registration.form.back}
                </Button>
                <Button
                    buttonType="success"
                    size="lg"
                    flex={1}
                    isLoading={isPatching}
                    onClick={handleNextClick}
                >
                    {t.registration.form.next}
                </Button>
            </HStack>
        </VStack>
    );
};
